<!-- Hidden, not destroyed, while a batch page is open in the outlet below: the rows stay in
     the DOM, so the filters, page and scroll offset are all still here when it closes. -->
<div class="transaction-list-container" [hidden]="childOpen">

    <!-- Hidden File Input -->
    <input
        #fileInput
        type="file"
        accept=".txt,.ach"
        (change)="onFileSelected($event)"
        style="display: none"
    />

    <!-- Filter Card -->
    <mat-card class="content-card filter-card">
        <mat-card-content>

            <!-- Card Header -->
            <div class="card-header">
                <h2 class="page-title">Payment Processing</h2>

                <div class="header-actions">
                    <!-- Global transaction search. An icon button, not a field: what it
                         returns are transactions, and this page lists batches — the results
                         need a surface of their own, so they get a dialog. -->
                    <button mat-icon-button type="button"
                            class="search-action"
                            aria-label="Search transactions by loan number or customer name"
                            matTooltip="Search transactions"
                            (click)="onSearchTransactions()">
                        <mat-icon>search</mat-icon>
                    </button>
                    <button mat-stroked-button type="button" (click)="onRunSanityCheck()">
                        <mat-icon>fact_check</mat-icon>
                        Run Sanity Check
                    </button>
                    <button mat-raised-button color="primary" type="button" (click)="onCreate()">
                        <mat-icon>upload_file</mat-icon>
                        Upload File
                    </button>
                    <button mat-raised-button color="primary" type="button" (click)="onCreateManual()">
                        <mat-icon>edit_note</mat-icon>
                        Manual Transaction
                    </button>
                    <button mat-stroked-button type="button" (click)="loadGroups()">
                        <mat-icon>refresh</mat-icon>
                        Refresh
                    </button>
                </div>
            </div>

            <form [formGroup]="filterForm" (ngSubmit)="onSearch()">
                <formly-form [form]="filterForm" [fields]="filterFields" [model]="filterModel"></formly-form>

                <!-- Clear (x) button shared by every filter field, rendered in the matSuffix slot.
                     `field` is the formly field passed in as template context. -->
                <ng-template #clearSuffix let-field="field">
                    <button *ngIf="field.formControl?.value"
                            mat-icon-button type="button"
                            aria-label="Clear filter"
                            (click)="$event.stopPropagation(); field.formControl?.setValue(null)">
                        <mat-icon>close</mat-icon>
                    </button>
                </ng-template>

                <!-- Filter Actions -->
                <div class="filter-actions">
                    <button mat-stroked-button type="button" (click)="onReset()">
                        <mat-icon>refresh</mat-icon>
                        Reset
                    </button>
                    <button mat-raised-button color="primary" type="submit" [disabled]="isLoading">
                        <mat-spinner *ngIf="isLoading" diameter="18" class="btn-spinner"></mat-spinner>
                        <mat-icon *ngIf="!isLoading">search</mat-icon>
                        Search
                    </button>
                </div>
            </form>
        </mat-card-content>
    </mat-card>

    <!-- Results Card -->
    <mat-card class="content-card list-card">
        <mat-card-content>

            <!-- Loading overlay -->
            <div class="loading-overlay" *ngIf="isLoading">
                <mat-spinner diameter="48"></mat-spinner>
            </div>

            <!-- Batch items. Each card places its own data via grid areas, so the layout
                 re-flows per breakpoint instead of being bound to a column order. -->
            <div class="batch-list" [class.is-loading]="isLoading">

                <mat-card *ngFor="let row of batches; trackBy: trackByBatchId"
                          appearance="outlined"
                          class="batch-card"
                          role="button"
                          tabindex="0"
                          [attr.aria-label]="'Open ' + fundingTypeLabel(row.fundingType) + ' batch created ' + batchDate(row)"
                          (click)="onRowClick(row)"
                          (keydown.enter)="onRowClick(row)">
                    <div class="batch-card-body">

                        <!-- Identity: file type, funding type, originator -->
                        <div class="bc-identity">
                            <span class="file-type-badge" [ngClass]="fileTypeBadgeClass(row.fileType)">
                                {{ row.fileType || 'EFT' }}
                            </span>
                            <!-- Funding type with the originator stacked under it, so the
                                 originator lines up with the funding type, not the badge. -->
                            <div class="bc-identity-text">
                                <span class="bc-funding">{{ fundingTypeLabel(row.fundingType) }}</span>
                                <span class="bc-originator" *ngIf="row.originatorId">{{ row.originatorId }}</span>
                            </div>
                        </div>

                        <!-- Dates -->
                        <div class="bc-dates">
                            <div class="bc-fact">
                                <span class="bc-fact-label">Created Date</span>
                                <span class="bc-fact-value">
                                    <!-- Same chip as the due dates, so both values start at
                                         the same x position. -->
                                    <span class="date-chip">{{ batchDate(row) }}</span>
                                </span>
                            </div>
                            <div class="bc-fact">
                                <span class="bc-fact-label">Due Date</span>
                                <!-- #dueValue is measured to fit as many chips as the cell
                                     holds; the rest collapse into "+N". -->
                                <span class="bc-fact-value bc-due-dates" #dueValue>
                                    <ng-container *ngIf="row.dueDates?.length; else noDueDates">
                                        <span class="date-chip" *ngFor="let dueDate of visibleDueDates(row)">
                                            {{ dueDate }}
                                        </span>
                                        <span class="due-date-more"
                                              *ngIf="hiddenDueDateCount(row) as hiddenCount"
                                              [matTooltip]="row.dueDates.join(', ')">
                                            +{{ hiddenCount }}
                                        </span>
                                    </ng-container>
                                    <ng-template #noDueDates>—</ng-template>
                                </span>
                            </div>
                        </div>

                        <!-- Total amount -->
                        <div class="bc-stat">
                            <span class="bc-stat-value">
                                {{ FormatUtil.formatCurrency(row.totalAmountInCents / 100) }}
                            </span>
                            <span class="bc-stat-label">Total Amount</span>
                        </div>

                        <!-- Status breakdown, carrying the transaction total as its headline -->
                        <div class="bc-status">
                            <lmc-status-breakdown
                                [items]="statusItems(row)"
                                [total]="row.totalTransaction"
                                totalLabel="Transactions">
                            </lmc-status-breakdown>
                        </div>

                    </div>
                </mat-card>

                <!-- Empty state -->
                <div class="empty-state" *ngIf="!isLoading && !batches.length">
                    <mat-icon>inbox</mat-icon>
                    <p>No transaction groups found</p>
                    <span>Try adjusting your filters or date range</span>
                </div>

            </div>

            <!-- Server-side Paginator -->
            <mat-paginator
                [length]="totalRows"
                [pageSize]="pageSize"
                [pageSizeOptions]="[10, 20, 50]"
                [pageIndex]="currentPage"
                showFirstLastButtons
                (page)="onPageChange($event)">
            </mat-paginator>

        </mat-card-content>
    </mat-card>

</div>

<!-- Batch details renders here, in this component's own outlet, instead of replacing it. -->
<router-outlet (activate)="onChildActivate()" (deactivate)="onChildDeactivate()"></router-outlet>
